import { interpolate, Easing } from 'remotion';
import {animationWindowFrames} from '@waka/compat/animation-window';
import {
  AnimationType,
  ItemAnimation,
  ANIMATION_META,
  ANIMATION_EASINGS,
  AnimationEasingKey,
  AnimationProperty,
  EasingFunction,
  AnimationPropertyAnimation,
} from './animation-item-type';
import {
  getPositionOffsetAt,
  type PositionKeyframe,
} from './position-keyframes';

interface CalculateAnimationParams {
  animationType: AnimationType;
  animationDurationInSeconds: number;
  currentFrame: number;
  framesPerSecond: number;
  totalDurationInFrames: number;
  customInputRanges?: Partial<Record<AnimationProperty, number[]>>;
  customOutputRanges?: Partial<Record<AnimationProperty, number[]>>;
  customEasings?: Partial<Record<AnimationProperty, EasingFunction>>;
}

interface CalculateAnimationsParams {
  animations: ItemAnimation[];
  currentFrame: number;
  framesPerSecond: number;
  totalDurationInFrames: number;
  // 위치 키프레임(아이템 로컬 프레임 기준) — 현재 프레임의 오프셋이 px translate 로 합성된다.
  positionKeyframes?: PositionKeyframe[];
}

interface AnimationStyle {
  transform?: string;
  opacity?: number;
  clipPath?: string;
  filter?: string;
}

// 기본 Easing 함수
const DEFAULT_EASING: EasingFunction = Easing.inOut(Easing.ease);

// clip inset 각 변 % 를 0~100 으로 clamp — 복수 애니 덧셈 병합 후 경계 보정.
const clampClipPercent = (value: number): number =>
  Math.min(100, Math.max(0, value));

const buildClipPath = (
  clipTop: number,
  clipRight: number,
  clipBottom: number,
  clipLeft: number,
): string | undefined => {
  if (clipTop === 0 && clipRight === 0 && clipBottom === 0 && clipLeft === 0) {
    return undefined;
  }
  return `inset(${clampClipPercent(clipTop)}% ${clampClipPercent(clipRight)}% ${clampClipPercent(clipBottom)}% ${clampClipPercent(clipLeft)}%)`;
};

/**
 * inputRange를 현재 프레임과 duration에 맞춰 실제 프레임 범위로 변환하는 유틸리티 함수
 *
 * @param inputRange - 원본 keyframe 배열 (예: [0, 15])
 * @param startFrame - 애니메이션 시작 프레임
 * @param durationInFrames - 애니메이션 지속 프레임 수
 * @returns 스케일링된 실제 프레임 범위
 */
const normalizeInputRange = (
  inputRange: number[],
  startFrame: number,
  durationInFrames: number,
): number[] => {
  const maxInput = inputRange[inputRange.length - 1] ?? 1;
  return inputRange.map(
    (frame) => startFrame + (frame / maxInput) * durationInFrames,
  );
};

/**
 * 속성별 outputRange 를 결정하는 유틸리티 함수
 *
 * customOutputRanges 가 있으면 그 값을, 없으면 메타 기본값을 쓴다.
 * remotion interpolate 는 inputRange 와 outputRange 의 길이가 다르면 throw 하므로,
 * 길이가 안 맞는 커스텀 값은 기본값으로 폴백한다. 기본값마저 길이가 안 맞으면(커스텀
 * inputRange 로 keyframe 수가 바뀐 경우) null 을 돌려 해당 property 를 건너뛴다 — 렌더 크래시 방지.
 */
const resolveOutputRange = (
  propAnim: AnimationPropertyAnimation,
  inputRange: number[],
  customOutputRange?: number[],
): number[] | null => {
  if (customOutputRange && customOutputRange.length === inputRange.length) {
    return customOutputRange;
  }
  return propAnim.outputRange.length === inputRange.length
    ? propAnim.outputRange
    : null;
};

/**
 * 속성별 Easing 함수를 결정하는 유틸리티 함수
 *
 * 우선순위: customEasing(함수, remotion-gen 주입) > easingKey(사용자가 UI 에서 고른 곡선)
 *          > propAnim.easing(타입 기본) > meta.defaultEasing > DEFAULT_EASING
 */
const resolveEasing = (
  propAnim: AnimationPropertyAnimation,
  customEasing?: EasingFunction,
  metaDefaultEasing?: AnimationEasingKey,
  easingKey?: AnimationEasingKey,
): EasingFunction => {
  // propAnim.easing·metaDefaultEasing 은 이제 함수가 아니라 AnimationEasingKey 키다
  // (ANIMATION_META 가 remotion-free 데이터 → easing 을 키로 저장). 여기서 함수로 해소한다.
  return (
    customEasing ??
    (easingKey ? ANIMATION_EASINGS[easingKey] : undefined) ??
    (propAnim.easing ? ANIMATION_EASINGS[propAnim.easing] : undefined) ??
    (metaDefaultEasing ? ANIMATION_EASINGS[metaDefaultEasing] : undefined) ??
    DEFAULT_EASING
  );
};

/**
 * 단일 Animation 타입에 따른 스타일 계산 (기존 호환성 유지)
 */
export const calculateAnimationStyle = ({
  animationType,
  animationDurationInSeconds,
  currentFrame,
  framesPerSecond,
  totalDurationInFrames,
  customInputRanges,
  customOutputRanges,
  customEasings,
}: CalculateAnimationParams): AnimationStyle => {
  const meta = ANIMATION_META[animationType];
  const durationInFrames = animationDurationInSeconds * framesPerSecond;

  let scale = 1;
  let opacity = 1;
  let rotation = 0;
  let translateX = 0;
  let translateY = 0;
  let translateXPx = 0;
  let translateYPx = 0;
  let clipLeft = 0;
  let clipRight = 0;
  let clipTop = 0;
  let clipBottom = 0;
  let blur = 0;

  // 카테고리별 적용 시점 결정
  let startFrame = 0;
  if (meta.category === 'out') {
    startFrame = totalDurationInFrames - durationInFrames;
  }
  // combo는 startFrame = 0 (시작부터)

  meta.properties.forEach((propAnim) => {
    // customInputRanges가 있으면 사용, 없으면 기본값
    const inputRange =
      customInputRanges?.[propAnim.property] ?? propAnim.inputRange;

    // customOutputRanges가 있으면 사용, 없으면 기본값 (길이 불일치 시 폴백·스킵)
    const outputRange = resolveOutputRange(
      propAnim,
      inputRange,
      customOutputRanges?.[propAnim.property],
    );
    if (!outputRange) return;

    // inputRange를 실제 프레임으로 스케일링 (공통 유틸리티 함수 사용)
    const scaledInputRange = normalizeInputRange(
      inputRange,
      startFrame,
      durationInFrames,
    );

    // Easing 함수 결정 (공통 유틸리티 함수 사용)
    // meta가 satisfies로 인해 좁은 타입을 가지므로 타입 단언 필요
    const metaWithEasing = meta as { defaultEasing?: AnimationEasingKey };
    const easing = resolveEasing(
      propAnim,
      customEasings?.[propAnim.property],
      metaWithEasing.defaultEasing,
    );

    const value = interpolate(currentFrame, scaledInputRange, outputRange, {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing,
    });

    // AnimationProperty 타입으로 단언하여 모든 케이스 처리
    const property = propAnim.property as AnimationProperty;
    switch (property) {
      case 'scale':
        scale *= value;
        break;
      case 'opacity':
        opacity *= value;
        break;
      case 'rotation':
        rotation += value;
        break;
      case 'translateX':
        translateX += value;
        break;
      case 'translateY':
        translateY += value;
        break;
      case 'translateXPx':
        translateXPx += value;
        break;
      case 'translateYPx':
        translateYPx += value;
        break;
      case 'clipLeft':
        clipLeft += value;
        break;
      case 'clipRight':
        clipRight += value;
        break;
      case 'clipTop':
        clipTop += value;
        break;
      case 'clipBottom':
        clipBottom += value;
        break;
      case 'blur':
        blur += value;
        break;
    }
  });

  const result: AnimationStyle = {};

  // transform 문자열 생성 (순서: translate → scale → rotate)
  if (
    scale !== 1 ||
    rotation !== 0 ||
    translateX !== 0 ||
    translateY !== 0 ||
    translateXPx !== 0 ||
    translateYPx !== 0
  ) {
    const transforms: string[] = [];
    // translate를 먼저 적용 (레이아웃 기준점 유지). %(아이템 크기)와 px(캔버스)는
    // 별도 translate 로 합성된다.
    if (translateX !== 0 || translateY !== 0) {
      transforms.push(`translate(${translateX}%, ${translateY}%)`);
    }
    if (translateXPx !== 0 || translateYPx !== 0) {
      transforms.push(`translate(${translateXPx}px, ${translateYPx}px)`);
    }
    if (scale !== 1) transforms.push(`scale(${scale})`);
    if (rotation !== 0) transforms.push(`rotate(${rotation}deg)`);
    result.transform = transforms.join(' ');
  }

  const clipPath = buildClipPath(clipTop, clipRight, clipBottom, clipLeft);
  if (clipPath) {
    result.clipPath = clipPath;
  }

  if (blur > 0) {
    result.filter = `blur(${blur}px)`;
  }

  if (opacity !== 1) {
    result.opacity = opacity;
  }

  return result;
};

/**
 * 복수 Animation 의 현재 프레임 원시 값 병합.
 * Scale과 Opacity는 곱셈으로 병합, Rotation/Translate 계열은 덧셈으로 병합.
 * 스타일 문자열이 아니라 값이 필요한 곳(선택 outline 의 표시 추적 등)이 함께 쓴다.
 */
export interface AnimationValues {
  scale: number;
  opacity: number;
  rotation: number;
  /** 아이템 자기 크기 기준 % */
  translateX: number;
  translateY: number;
  /** 캔버스 px */
  translateXPx: number;
  translateYPx: number;
  clipLeft: number;
  clipRight: number;
  clipTop: number;
  clipBottom: number;
  /** filter blur px — 가산 병합 */
  blur: number;
}

export const calculateAnimationsValues = ({
  animations,
  currentFrame,
  framesPerSecond,
  totalDurationInFrames,
  positionKeyframes,
}: CalculateAnimationsParams): AnimationValues => {
  let combinedScale = 1;
  let combinedOpacity = 1;
  let combinedRotation = 0;
  let combinedTranslateX = 0;
  let combinedTranslateY = 0;
  let combinedTranslateXPx = 0;
  let combinedTranslateYPx = 0;
  let combinedClipLeft = 0;
  let combinedClipRight = 0;
  let combinedClipTop = 0;
  let combinedClipBottom = 0;
  let combinedBlur = 0;

  // 위치 키프레임 오프셋 — 애니 프리셋과 독립적으로 px translate 에 합산.
  if (positionKeyframes && positionKeyframes.length > 0) {
    const offset = getPositionOffsetAt(positionKeyframes, currentFrame);
    combinedTranslateXPx += offset.dx;
    combinedTranslateYPx += offset.dy;
  }

  animations.forEach((animation) => {
    // enabled가 false인 애니메이션은 건너뜀 (기본값은 true)
    if (animation.enabled === false) return;

    const meta = ANIMATION_META[animation.type] as
      | (typeof ANIMATION_META)[AnimationType]
      | undefined;
    // 폐기된 타입(구 move 등)이 저장 데이터에 남아 있으면 크래시 대신 무시한다.
    if (!meta) return;

    // 카테고리별 시작/끝 프레임 결정
    const {startFrame,endFrame}=animationWindowFrames(animation,framesPerSecond,totalDurationInFrames);
    // In: startFrame = 0, endFrame = durationInSeconds * fps (기본값)

    const durationInFrames = endFrame - startFrame;

    // 각 property별로 계산
    meta.properties.forEach((propAnim) => {
      // customInputRanges가 있으면 사용, 없으면 기본값
      const inputRange =
        animation.customInputRanges?.[propAnim.property] ?? propAnim.inputRange;

      // customOutputRanges가 있으면 사용, 없으면 기본값 (길이 불일치 시 폴백·스킵)
      const outputRange = resolveOutputRange(
        propAnim,
        inputRange,
        animation.customOutputRanges?.[propAnim.property],
      );
      if (!outputRange) return;

      // inputRange를 실제 프레임으로 스케일링 (공통 유틸리티 함수 사용)
      const scaledInputRange = normalizeInputRange(
        inputRange,
        startFrame,
        durationInFrames,
      );

      // Easing 함수 결정 (공통 유틸리티 함수 사용)
      // meta가 satisfies로 인해 좁은 타입을 가지므로 타입 단언 필요
      const metaWithEasing = meta as { defaultEasing?: AnimationEasingKey };
      const easing = resolveEasing(
        propAnim,
        animation.customEasings?.[propAnim.property],
        metaWithEasing.defaultEasing,
        animation.easings?.[propAnim.property],
      );

      const value = interpolate(currentFrame, scaledInputRange, outputRange, {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
        easing,
      });

      // AnimationProperty 타입으로 단언하여 모든 케이스 처리
      const property = propAnim.property as AnimationProperty;
      switch (property) {
        case 'scale':
          combinedScale *= value;
          break;
        case 'opacity':
          combinedOpacity *= value;
          break;
        case 'rotation':
          combinedRotation += value;
          break;
        case 'translateX':
          combinedTranslateX += value;
          break;
        case 'translateY':
          combinedTranslateY += value;
          break;
        case 'translateXPx':
          combinedTranslateXPx += value;
          break;
        case 'translateYPx':
          combinedTranslateYPx += value;
          break;
        case 'clipLeft':
          combinedClipLeft += value;
          break;
        case 'clipRight':
          combinedClipRight += value;
          break;
        case 'clipTop':
          combinedClipTop += value;
          break;
        case 'clipBottom':
          combinedClipBottom += value;
          break;
        case 'blur':
          combinedBlur += value;
          break;
      }
    });
  });

  return {
    scale: combinedScale,
    opacity: combinedOpacity,
    rotation: combinedRotation,
    translateX: combinedTranslateX,
    translateY: combinedTranslateY,
    translateXPx: combinedTranslateXPx,
    translateYPx: combinedTranslateYPx,
    clipLeft: combinedClipLeft,
    clipRight: combinedClipRight,
    clipTop: combinedClipTop,
    clipBottom: combinedClipBottom,
    blur: combinedBlur,
  };
};

/**
 * 복수 Animation 스타일 계산
 * Scale과 Opacity는 곱셈으로 병합, Rotation/TranslateX/TranslateY는 덧셈으로 병합
 */
export const calculateAnimationsStyle = (
  params: CalculateAnimationsParams,
): AnimationStyle => {
  const values = calculateAnimationsValues(params);

  const result: AnimationStyle = {};

  // transform 문자열 생성 (순서: translate → scale → rotate)
  if (
    values.scale !== 1 ||
    values.rotation !== 0 ||
    values.translateX !== 0 ||
    values.translateY !== 0 ||
    values.translateXPx !== 0 ||
    values.translateYPx !== 0
  ) {
    const transforms: string[] = [];
    // translate를 먼저 적용 (레이아웃 기준점 유지). %(아이템 크기)와 px(캔버스)는
    // 별도 translate 로 합성된다.
    if (values.translateX !== 0 || values.translateY !== 0) {
      transforms.push(
        `translate(${values.translateX}%, ${values.translateY}%)`,
      );
    }
    if (values.translateXPx !== 0 || values.translateYPx !== 0) {
      transforms.push(
        `translate(${values.translateXPx}px, ${values.translateYPx}px)`,
      );
    }
    if (values.scale !== 1) transforms.push(`scale(${values.scale})`);
    if (values.rotation !== 0) transforms.push(`rotate(${values.rotation}deg)`);
    result.transform = transforms.join(' ');
  }

  const clipPath = buildClipPath(
    values.clipTop,
    values.clipRight,
    values.clipBottom,
    values.clipLeft,
  );
  if (clipPath) {
    result.clipPath = clipPath;
  }

  if (values.blur > 0) {
    result.filter = `blur(${values.blur}px)`;
  }

  if (values.opacity !== 1) {
    result.opacity = values.opacity;
  }

  return result;
};

/**
 * Animation 스타일을 기존 스타일과 병합
 */
export const mergeAnimationStyle = (
  baseStyle: React.CSSProperties,
  animationStyle: AnimationStyle,
): React.CSSProperties => {
  const merged = { ...baseStyle };

  // transform 병합
  if (animationStyle.transform) {
    merged.transform = baseStyle.transform
      ? `${baseStyle.transform} ${animationStyle.transform}`
      : animationStyle.transform;
  }

  // ponytail: clip-path 는 CSS 상 하나뿐이라 base 와 합성 불가 — 등장 구간에만 잠깐
  // 적용되는 애니 값을 우선한다(crop 은 video/image 전용이라 실충돌 없음).
  if (animationStyle.clipPath) {
    merged.clipPath = animationStyle.clipPath;
  }

  // filter 병합 — CSS filter 는 공백 구분 다중 함수 허용.
  if (animationStyle.filter) {
    merged.filter = baseStyle.filter
      ? `${baseStyle.filter} ${animationStyle.filter}`
      : animationStyle.filter;
  }

  // opacity 병합 (곱셈)
  if (animationStyle.opacity !== undefined) {
    const baseOpacity =
      typeof baseStyle.opacity === 'number' ? baseStyle.opacity : 1;
    merged.opacity = baseOpacity * animationStyle.opacity;
  }

  return merged;
};
